import { Component, h, Prop, Watch, Host, Element, State } from '@stencil/core';
import debounce from 'debounce';
import { isMagnoliaEditingMode } from '../../helper';

interface UiCmsSliderBody {
  slidesPerPage?: number;
  slidesPerMove?: number;
  loop?: boolean;
  autoplay?: boolean;
  fullWidth: boolean;
}

@Component({
  tag: 'ui-cms-slider',
  styleUrls: { default: 'ui-cms-slider.scss', editing: 'ui-cms-slider.magnolia.scss' },
  shadow: false,
})
export class UiCmsSlider {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsSliderBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop() inverted: boolean;

  @State() isMobile = false;

  @State() isLoaded = false;

  private resizeObserver: ResizeObserver;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
    if (this.inverted) this.setInvertedState();
  }

  componentDidLoad() {
    this.addResizeObserver();
    this.isLoaded = true;
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private addResizeObserver() {
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.isMobile = window.innerWidth < 1024;
      }, 200)
    );

    this.resizeObserver.observe(this.el);
  }

  private countCarouselItems(slidesPerPage: number, slidesPerMove: number): number {
    const totalItems = this.el.querySelectorAll('sd-carousel-item').length;

    if (slidesPerPage) {
      const move = slidesPerMove || slidesPerPage;
      return Math.ceil((totalItems - slidesPerPage) / move) + 1;
    }
    return totalItems;
  }

  private checkForCarouselItems(): boolean {
    return !!this.el.querySelector('sd-carousel-item');
  }

  private isInsideContainer(): boolean {
    let parent = this.el.parentElement;
    while (parent) {
      if (parent.tagName.toLowerCase() === 'ui-cms-container') {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  }

  private getSdClass(element: HTMLElement): string {
    const classes = element.className.split(/\s+/);
    // Match class names starting with 'sd-' and without additional modifiers
    const sdClass = classes.find((cls) => /^sd-[a-zA-Z0-9]+$/.test(cls));
    return sdClass;
  }

  private setInvertedState(): void {
    Array.from(this.el.children).forEach((element: HTMLElement) => {
      this.applyInverted(element);
      this.applyInverted(element?.children[0] as HTMLElement);
    });
  }

  private applyInverted(element: HTMLElement): void {
    if (element) {
      const sdClass = this.getSdClass(element);
      if (sdClass) {
        const invertedClass = `${sdClass}--inverted`;
        element.classList.add(invertedClass);
      } else {
        element.setAttribute('inverted', 'true');
      }
    }
  }

  render() {
    const slidesPerPage = this.isMobile ? 1 : this.body.slidesPerPage;
    const slidesPerMove = this.isMobile ? 1 : this.body.slidesPerMove;
    const isInsideContainer = this.isInsideContainer();
    const totalSlides = this.countCarouselItems(slidesPerPage, slidesPerMove);
    return (
      <Host class={{ 'navigation-hide': totalSlides === 1 }}>
        <slot name="quality-seal"></slot>
        {this.checkForCarouselItems() && (
          <sd-carousel
            class={{
              'full-width': this.body.fullWidth && !isInsideContainer,
              'inside-container': isInsideContainer,
            }}
            slides-per-page={slidesPerPage}
            slides-per-move={slidesPerMove}
            variant={totalSlides > 8 ? 'number' : 'dot'}
            inverted={this.inverted}
            loop={this.isLoaded && this.body.loop && !isMagnoliaEditingMode()}
            autoplay={totalSlides > 1 && this.body.autoplay}
          >
            <slot></slot>
          </sd-carousel>
        )}
      </Host>
    );
  }
}
